import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {Role} from "../customer-role.model";
import {CustomerService} from "../customer.service";
import {MessageService} from "primeng/api";
import {Customer} from "../customer.model";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';

interface PermissionGroup {
    name: string;
    permissions: { permissionCode: string; permissionName: string; description?: string }[];
}

@Component({
    selector: 'app-customer-roles',
    templateUrl: './customer-roles.component.html',
    styleUrls: ['./customer-roles.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ToastModule,
        ButtonModule,
        TableModule,
        DialogModule,
        CheckboxModule,
        InputTextModule,
        TagModule
    ],
    providers: [
        MessageService,
        CustomerService,
    ]
})
export class CustomerRolesComponent implements OnInit, OnChanges {
    @Input() displayModal: boolean = false;
    @Input() dialogHeader: string = '';
    @Input() customer: Customer;
    @Output() modalClosed = new EventEmitter<boolean>();

    availableRoles: Role[] = [];
    selectedRole: Role | null = null;
    activeRole: Role | null = null;
    searchTerm = '';
    isLoading = false;
    isSaving = false;

    private readonly hiddenRoleNames = new Set(['ADMIN', 'OPERATIONS_MANAGER', 'SUPPORT']);

    constructor(
        private customerService: CustomerService,
        private messageService: MessageService
    ) { }

    ngOnInit(): void {
        this.loadAvailableRoles();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.displayModal) {
            if (!this.availableRoles.length) {
                this.loadAvailableRoles();
            } else if (this.hasCustomerId()) {
                this.loadCustomerRoles();
            }
        }
    }

    loadAvailableRoles() {
        this.isLoading = true;
        this.customerService.getAllRoles().subscribe(roles => {
            this.availableRoles = (roles ?? [])
                .filter(role => this.isAssignableCompanyRole(role))
                .sort((left, right) => left.roleName.localeCompare(right.roleName));
            if (this.displayModal && this.hasCustomerId()) {
                this.loadCustomerRoles();
            } else {
                this.activeRole = this.filteredRoles[0] ?? null;
            }
            this.isLoading = false;
        }, error => {
            console.error('Error loading roles:', error);
            this.isLoading = false;
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Unable to load roles.'});
        });
    }

    loadCustomerRoles() {
        if (!this.hasCustomerId()) {
            this.selectedRole = null;
            this.activeRole = this.filteredRoles[0] ?? null;
            return;
        }

        this.customerService.getRolesByCustomerId(this.customer.id).subscribe(roles => {
            const visibleRoles = (roles ?? []).filter(role => this.isAssignableCompanyRole(role));
            this.customer.roles = visibleRoles;
            this.selectedRole = visibleRoles[0] ?? null;
            this.activeRole = this.selectedRole ?? this.filteredRoles[0] ?? null;
        });
    }

    get filteredRoles(): Role[] {
        const query = this.searchTerm.trim().toLowerCase();
        if (!query) {
            return this.availableRoles;
        }
        return this.availableRoles.filter(role =>
            role.roleName.toLowerCase().includes(query) ||
            (role.description ?? '').toLowerCase().includes(query) ||
            (role.permissions ?? []).some(permission =>
                permission.permissionName?.toLowerCase().includes(query) ||
                permission.permissionCode?.toLowerCase().includes(query)
            )
        );
    }

    get selectedPermissionCount(): number {
        return this.getPermissionCount(this.selectedRole);
    }

    get activePermissionGroups(): PermissionGroup[] {
        const grouped = new Map<string, PermissionGroup>();
        for (const permission of this.getRolePermissions(this.activeRole)) {
            const groupName = this.getPermissionGroupName(permission.permissionName, permission.permissionCode);
            if (!grouped.has(groupName)) {
                grouped.set(groupName, {name: groupName, permissions: []});
            }
            grouped.get(groupName)?.permissions.push(permission);
        }
        return Array.from(grouped.values())
            .map(group => ({
                ...group,
                permissions: group.permissions.sort((left, right) => left.permissionName.localeCompare(right.permissionName))
            }))
            .sort((left, right) => left.name.localeCompare(right.name));
    }

    selectRole(role: Role) {
        this.selectedRole = role;
        this.activeRole = role;
    }

    previewRole(role: Role) {
        this.activeRole = role;
    }

    onSave() {
        if (!this.selectedRole || this.isSaving || !this.hasCustomerId()) {
            return;
        }

        this.isSaving = true;
        this.customerService.modifyRolesForCustomer(this.customer.id, [this.selectedRole])
            .subscribe(roles => {
                this.customer.roles = roles ? [...roles] : [this.selectedRole as Role];
                this.messageService.add({severity: 'success', summary: 'Success', detail: 'Role updated successfully.'});
                this.isSaving = false;
                this.closeModal();
            }, error => {
                console.error('Error updating role:', error);
                this.isSaving = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update role. Please try again later.'
                });
            });
    }

    onCancel() {
        this.closeModal();
    }

    closeModal() {
        this.displayModal = false;
        this.modalClosed.emit(true);
    }

    isSelected(role: Role): boolean {
        return this.selectedRole?.roleId === role.roleId;
    }

    getPermissionCount(role: Role | null | undefined): number {
        return this.getRolePermissions(role).length;
    }

    private getRolePermissions(role: Role | null | undefined): PermissionGroup['permissions'] {
        return Array.isArray(role?.permissions) ? role.permissions : [];
    }

    private hasCustomerId(): boolean {
        return Number.isFinite(Number(this.customer?.id));
    }

    private isAssignableCompanyRole(role: Role | null | undefined): role is Role {
        if (!role?.roleId || this.hiddenRoleNames.has(role.roleName)) {
            return false;
        }
        const scope = (role.roleScope || '').trim().toUpperCase();
        if (scope === 'COMPANY_GROUP' || scope === 'CUSTOMER_PORTAL') {
            return false;
        }
        return !this.hasPortalPermission(role);
    }

    private hasPortalPermission(role: Role): boolean {
        return (role.permissions ?? []).some(permission =>
            (permission.permissionCode || '').trim().toLowerCase().startsWith('portal.')
        );
    }

    private getPermissionGroupName(permissionName?: string, permissionCode?: string): string {
        if (permissionName?.includes(':')) {
            return permissionName.split(':')[0].trim();
        }
        return (permissionCode ?? 'General').split('.')[0]
            .replace(/-/g, ' ')
            .replace(/\b\w/g, letter => letter.toUpperCase());
    }
}
